import { useCallback, useMemo } from "react"

import { useAgentsList } from "@/agents/catalog"

import { TRACKING_EVENTS, useAnalytics } from "@/lib/analytics"
import { useQueryParams } from "@/hooks/useQueryParams"

export const useAgentFilter = () => {
  const queryParams = useQueryParams()
  const { trackEvent } = useAnalytics()

  const agentId = queryParams.get("agent_id")
  const { data: agents } = useAgentsList()

  const agentData = useMemo(() => {
    if (!agentId || !agents) {
      return null
    }
    return agents.find((agent) => agent.id === agentId) || null
  }, [agentId, agents])

  const setAgentFilter = useCallback(
    (id: string) => {
      queryParams.set("agent_id", id)
      trackEvent(TRACKING_EVENTS.AGENTS.FILTER_APPLIED, {
        agent_id: id,
        agent_display_name: agents?.find((agent) => agent.id === id)
          ?.display_name,
      })
    },
    [agents, trackEvent, queryParams],
  )

  const clearAgentFilter = useCallback(() => {
    queryParams.remove("agent_id")
    trackEvent(TRACKING_EVENTS.AGENTS.FILTER_CLEARED, {
      previous_agent_id: agentId,
    })
  }, [agentId, trackEvent, queryParams])

  const hasAgentFilter = useMemo(() => {
    return agentId !== null && agentId !== undefined && agentId.trim() !== ""
  }, [agentId])

  return {
    agentId,
    agentData,
    agents: agents || [],
    setAgentFilter,
    clearAgentFilter,
    hasAgentFilter,
  }
}
